import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
  cn,
} from '@cca/ui';
import { AlertTriangle } from 'lucide-react';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { useRuntimeConfig } from '../lib/runtime-config.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface ThreadSummary {
  readonly id: string;
  readonly subject: string | null;
  readonly status: string;
  readonly classification: string | null;
  readonly associationState: string;
  readonly injectionSuspected: boolean;
  readonly fromAddress: string | null;
  readonly lastInboundAt: string | null;
  readonly messageCount: number;
}

interface AssociationCandidate {
  readonly kind: string;
  readonly id: string;
  readonly label: string;
  readonly confidence: number;
  readonly reasons: readonly string[];
}

interface EvidenceItem {
  readonly label: string;
  readonly value: string;
  readonly source: string;
}

interface ThreadDetail extends ThreadSummary {
  readonly language: string | null;
  readonly associationReasons: readonly string[];
  readonly associationCandidates: readonly AssociationCandidate[];
  readonly injectionSignals: readonly string[];
  readonly messages: readonly {
    id: string;
    direction: string;
    fromAddress: string;
    fromName: string | null;
    subject: string | null;
    bodyText: string;
    receivedAt: string;
  }[];
  readonly drafts: readonly {
    id: string;
    subject: string | null;
    bodyText: string;
    language: string;
    status: string;
    riskLevel: string;
    requiresApproval: boolean;
    evidence: readonly EvidenceItem[];
    missingInformation: readonly string[];
    createdByAgent: boolean;
  }[];
  readonly order: {
    id: string;
    name: string;
    financialStatus: string;
    fulfilmentStatus: string;
  } | null;
}

const ASSOCIATION_TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  MATCHED: 'success',
  CONFIRMED: 'success',
  AMBIGUOUS: 'warning',
  UNMATCHED: 'neutral',
};

const ESCALATED_CLASSIFICATIONS = new Set([
  'REFUND_REQUEST',
  'CANCELLATION_REQUEST',
  'COMPLAINT',
  'ADDRESS_CHANGE_REQUEST',
]);

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 60) return `${Math.max(0, minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function humanise(value: string | null): string {
  return value ? value.replaceAll('_', ' ').toLowerCase() : 'unclassified';
}

function ThreadListItem({
  thread,
  selected,
  onSelect,
}: {
  readonly thread: ThreadSummary;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'w-full border-b border-slate-200 px-3 py-3 text-left transition-colors',
        'focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none',
        'dark:border-slate-800',
        selected ? 'bg-sky-50 dark:bg-sky-950' : 'hover:bg-slate-50 dark:hover:bg-slate-900',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
          {thread.fromAddress ?? 'unknown sender'}
        </span>
        <span className="shrink-0 text-xs text-slate-400">
          {relativeTime(thread.lastInboundAt)}
        </span>
      </div>

      <p className="mt-0.5 truncate text-sm text-slate-600 dark:text-slate-400">
        {thread.subject ?? '(no subject)'}
      </p>

      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <Badge
          tone={ESCALATED_CLASSIFICATIONS.has(thread.classification ?? '') ? 'danger' : 'info'}
        >
          {humanise(thread.classification)}
        </Badge>
        <Badge tone={ASSOCIATION_TONE[thread.associationState] ?? 'neutral'}>
          {thread.associationState.toLowerCase()}
        </Badge>
        {thread.injectionSuspected ? <Badge tone="danger">injection</Badge> : null}
      </div>
    </button>
  );
}

function AmbiguityPanel({
  thread,
  accessToken,
  onConfirmed,
}: {
  readonly thread: ThreadDetail;
  readonly accessToken: string | null;
  readonly onConfirmed: () => void;
}) {
  const confirm = useMutation({
    mutationFn: (candidate: AssociationCandidate) =>
      apiRequest<{ status: string }>(`/api/inbox/threads/${thread.id}/confirm`, {
        method: 'POST',
        accessToken,
        body:
          candidate.kind === 'order'
            ? { orderId: candidate.id, customerId: null }
            : { orderId: null, customerId: candidate.id },
      }),
    onSuccess: onConfirmed,
  });

  return (
    <Card className="border-amber-300 dark:border-amber-700">
      <CardHeader>
        <CardTitle>This message needs a person to choose</CardTitle>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          More than one record matched equally well, so nothing was associated automatically. A
          wrong association would produce a confidently wrong reply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {thread.associationCandidates.map((candidate) => (
          <div
            key={`${candidate.kind}-${candidate.id}`}
            className="rounded-md border border-slate-200 p-3 dark:border-slate-800"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {candidate.label}
                  <span className="ml-2 text-xs font-normal text-slate-500">{candidate.kind}</span>
                </p>
                <ul className="mt-1 space-y-0.5">
                  {candidate.reasons.map((reason, index) => (
                    <li key={index} className="text-xs text-slate-600 dark:text-slate-400">
                      • {reason}
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate(candidate)}
              >
                Confirm this match
              </Button>
            </div>
          </div>
        ))}

        {confirm.isError ? (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {confirm.error instanceof ApiError ? confirm.error.message : 'Could not confirm.'}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ThreadDetailPane({
  threadId,
  accessToken,
}: {
  readonly threadId: string;
  readonly accessToken: string | null;
}) {
  const queryClient = useQueryClient();
  const [openEvidence, setOpenEvidence] = useState<string | null>(null);

  const thread = useQuery({
    queryKey: ['inbox-thread', threadId],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<ThreadDetail>(`/api/inbox/threads/${threadId}`, { accessToken }),
  });

  if (thread.isPending) return <LoadingState label="Loading conversation" />;
  if (thread.isError) {
    return (
      <ErrorState
        description={
          thread.error instanceof ApiError ? thread.error.message : 'Could not load the thread.'
        }
        onRetry={() => void thread.refetch()}
      />
    );
  }

  const data = thread.data;

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {data.subject ?? '(no subject)'}
        </h2>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <span>{humanise(data.classification)}</span>
          <span aria-hidden="true">·</span>
          <span>{data.status.toLowerCase()}</span>
          {data.language ? (
            <>
              <span aria-hidden="true">·</span>
              <span>language {data.language}</span>
            </>
          ) : null}
        </p>
      </header>

      {data.injectionSuspected ? (
        <div
          role="alert"
          className="flex gap-2 rounded-md bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950 dark:text-red-200"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            This message contains text resembling instructions to the assistant
            {data.injectionSignals.length > 0 ? ` (${data.injectionSignals.join(', ')})` : ''}. It
            was treated as customer data, never as an instruction, and the thread was escalated.
          </span>
        </div>
      ) : null}

      {data.associationState === 'AMBIGUOUS' && data.associationCandidates.length > 0 ? (
        <AmbiguityPanel
          thread={data}
          accessToken={accessToken}
          onConfirmed={() => {
            void queryClient.invalidateQueries({ queryKey: ['inbox-thread', threadId] });
            void queryClient.invalidateQueries({ queryKey: ['inbox-threads'] });
          }}
        />
      ) : null}

      {data.order ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {data.order.name}
            </span>
            <Badge tone="neutral">{data.order.financialStatus.toLowerCase()}</Badge>
            <Badge tone="neutral">{data.order.fulfilmentStatus.toLowerCase()}</Badge>
          </CardContent>
        </Card>
      ) : null}

      {data.associationReasons.length > 0 ? (
        <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-950">
          <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
            How this was associated
          </p>
          <ul className="mt-1 space-y-0.5">
            {data.associationReasons.map((reason, index) => (
              <li key={index} className="text-xs text-slate-600 dark:text-slate-400">
                • {reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <section aria-label="Messages" className="space-y-3">
        {data.messages.map((message) => (
          <Card key={message.id}>
            <CardHeader>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <CardTitle>{message.fromName ?? message.fromAddress}</CardTitle>
                <span className="text-xs text-slate-400">{relativeTime(message.receivedAt)}</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {message.direction.toLowerCase()} · {message.fromAddress}
              </p>
            </CardHeader>
            <CardContent>
              {/*
                Plain text only. The stored HTML was sanitised at ingestion, but
                rendering text means no sanitiser bug can ever become an XSS in
                the staff dashboard.
              */}
              <p className="text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">
                {message.bodyText}
              </p>
            </CardContent>
          </Card>
        ))}
      </section>

      {data.drafts.length > 0 ? (
        <section aria-label="Drafts" className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Drafts</h3>
          {data.drafts.map((draft) => (
            <Card key={draft.id}>
              <CardHeader className="flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle>{draft.subject ?? '(no subject)'}</CardTitle>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {draft.createdByAgent ? 'Drafted by the agent' : 'Drafted by a person'} ·{' '}
                    {draft.language}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Badge tone={draft.riskLevel === 'LOW' ? 'neutral' : 'warning'}>
                    {draft.riskLevel}
                  </Badge>
                  {draft.requiresApproval ? (
                    <Badge tone="warning">Draft · approval required</Badge>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="rounded-md border border-slate-200 bg-white p-3 text-sm whitespace-pre-wrap text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
                  {draft.bodyText}
                </p>

                {draft.missingInformation.length > 0 ? (
                  <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-950">
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Not known yet — deliberately not filled in
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {draft.missingInformation.map((item, index) => (
                        <li key={index} className="text-xs text-slate-600 dark:text-slate-400">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {draft.evidence.length > 0 ? (
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-0"
                      aria-expanded={openEvidence === draft.id}
                      onClick={() => setOpenEvidence(openEvidence === draft.id ? null : draft.id)}
                    >
                      {openEvidence === draft.id
                        ? 'Hide facts used'
                        : `Show facts used (${draft.evidence.length})`}
                    </Button>

                    {openEvidence === draft.id ? (
                      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 rounded-md bg-slate-50 p-3 sm:grid-cols-2 dark:bg-slate-950">
                        {draft.evidence.map((item, index) => (
                          <div key={index} className="flex justify-between gap-3 text-xs">
                            <dt className="text-slate-500 dark:text-slate-400">{item.label}</dt>
                            <dd className="text-right font-medium text-slate-800 dark:text-slate-200">
                              {item.value}
                              <span className="ml-1 font-normal text-slate-400">
                                ({item.source})
                              </span>
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </section>
      ) : null}
    </div>
  );
}

function InboxPage() {
  const config = useRuntimeConfig();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<'ALL' | 'AMBIGUOUS' | 'MATCHED' | 'UNMATCHED'>('ALL');

  const threads = useQuery({
    queryKey: ['inbox-threads', filter],
    enabled: Boolean(accessToken),
    queryFn: () =>
      apiRequest<{ threads: ThreadSummary[]; total: number }>(
        `/api/inbox/threads?limit=100${filter === 'ALL' ? '' : `&associationState=${filter}`}`,
        { accessToken },
      ),
  });

  const ingest = useMutation({
    mutationFn: () =>
      apiRequest<{ received: number; created: number; duplicates: number; ambiguous: number }>(
        '/api/inbox/ingest',
        { method: 'POST', accessToken },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbox-threads'] }),
  });

  return (
    <div className="mx-auto flex h-full max-w-6xl flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Inbox</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Customer conversations, with the order they relate to and why we think so.
          </p>
        </div>

        {/* Ingesting fabricated mail is only meaningful, and only permitted, in
            simulator mode — so the control simply does not exist otherwise. */}
        {config.simulatorEnabled ? (
          <Button variant="outline" disabled={ingest.isPending} onClick={() => ingest.mutate()}>
            {ingest.isPending ? 'Ingesting…' : 'Ingest simulated inbox'}
          </Button>
        ) : null}
      </header>

      {ingest.isSuccess ? (
        <div
          role="status"
          className="rounded-md bg-sky-50 p-3 text-sm text-sky-900 dark:bg-sky-950 dark:text-sky-200"
        >
          Received {ingest.data.received}; created {ingest.data.created}; skipped{' '}
          {ingest.data.duplicates} already seen; {ingest.data.ambiguous} need a person to choose.
        </div>
      ) : null}

      {ingest.isError ? (
        <ErrorState
          title="Ingest failed"
          description={
            ingest.error instanceof ApiError ? ingest.error.message : 'Unexpected error.'
          }
          onRetry={() => ingest.mutate()}
        />
      ) : null}

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by association">
        {(['ALL', 'AMBIGUOUS', 'MATCHED', 'UNMATCHED'] as const).map((state) => (
          <Button
            key={state}
            size="sm"
            variant={filter === state ? 'primary' : 'outline'}
            aria-pressed={filter === state}
            onClick={() => setFilter(state)}
          >
            {state}
          </Button>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
        <Card className="min-h-0 overflow-y-auto p-0">
          {threads.isPending ? <LoadingState label="Loading threads" /> : null}

          {threads.isError ? (
            <ErrorState
              description={
                threads.error instanceof ApiError
                  ? threads.error.message
                  : 'Could not load the inbox.'
              }
              onRetry={() => void threads.refetch()}
            />
          ) : null}

          {threads.data && threads.data.threads.length === 0 ? (
            <EmptyState
              title="No conversations"
              description={
                config.simulatorEnabled
                  ? 'Use "Ingest simulated inbox" to load the development dataset.'
                  : 'Nothing has arrived in the connected mailbox yet.'
              }
            />
          ) : null}

          <ul>
            {(threads.data?.threads ?? []).map((thread) => (
              <li key={thread.id}>
                <ThreadListItem
                  thread={thread}
                  selected={selected === thread.id}
                  onSelect={() => setSelected(thread.id)}
                />
              </li>
            ))}
          </ul>
        </Card>

        <div className="min-h-0 overflow-y-auto">
          {selected ? (
            <ThreadDetailPane threadId={selected} accessToken={accessToken} />
          ) : (
            <Card>
              <EmptyState
                title="Select a conversation"
                description="Choose a thread on the left to see its messages, the order it relates to, and any drafts."
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  component: () => (
    <RequireSession>
      <InboxPage />
    </RequireSession>
  ),
});
